import React, { useRef } from "react";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { uploadProfilePicture } from "../../services/api/digitalAsset";
import { showToast } from "../../store/toast/toastSlice";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ["image/jpeg", "image/png"];

const getInitials = (firstName, lastName) =>
  `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();

// Click-to-upload avatar shared by the profile settings and complete-profile pages.
// `size` accepts a number or an MUI responsive object, e.g. { xs: 64, sm: 90 }.
const ProfilePictureAvatar = ({
  profilePicture,
  refetchMe,
  firstName,
  lastName,
  size = 104,
  variant = "circular",
  showEditBadge = true,
  className = "",
}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const { isPending: isUploading, mutateAsync: uploadProfilePictureMutation } = useMutation({
    mutationFn: uploadProfilePicture,
    enabled: false,
  });

  const toastError = (message) => dispatch(showToast({ message, type: "error" }));

  const openFilePicker = () => {
    if (isUploading) return;
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    // Reset so picking the same file again still fires a change event.
    event.target.value = "";

    // No file means the picker was dismissed - nothing to report.
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      toastError("Unsupported file format. Only JPG and PNG are allowed.");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toastError("Image is too large. Maximum size is 5MB.");
      return;
    }

    try {
      await uploadProfilePictureMutation(file);
      dispatch(
        showToast({ message: "Profile picture updated successfully", type: "success" })
      );
      refetchMe();
    } catch (error) {
      toastError(
        error?.response?.data?.message ||
          "Failed to upload profile picture. Please try again."
      );
    }
  };

  const initials = getInitials(firstName, lastName);
  const roundedClass = variant === "square" ? "rounded-xl" : "rounded-full";

  return (
    <div
      role="button"
      tabIndex={0}
      className={`relative group shrink-0 inline-block ${roundedClass} ${
        isUploading ? "cursor-wait" : "cursor-pointer"
      } ${className}`}
      onClick={openFilePicker}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") openFilePicker();
      }}
      title={profilePicture?.url ? "Change profile picture" : "Upload profile picture"}
    >
      <Avatar
        variant={variant}
        className={roundedClass}
        src={profilePicture?.url || undefined}
        alt={profilePicture?.filename || "Profile picture"}
        sx={{
          width: size,
          height: size,
          fontSize: typeof size === "number" ? size / 3 : 28,
        }}
      >
        {initials || <i className="fa-regular fa-user text-3xl"></i>}
      </Avatar>

      <div
        className={`absolute inset-0 ${roundedClass} bg-black/45 flex items-center justify-center transition-opacity ${
          isUploading ? "opacity-100" : "opacity-0 group-hover:opacity-100"
        }`}
      >
        {isUploading ? (
          <CircularProgress size={26} sx={{ color: "#fff" }} />
        ) : (
          <i className="fa-regular fa-camera text-white text-xl"></i>
        )}
      </div>

      {showEditBadge && !isUploading && (
        <span className="absolute bottom-0 right-0 h-6 w-6 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center">
          <i className="fa-light fa-pencil text-gray-900 text-[11px]"></i>
        </span>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png"
        className="hidden"
        onChange={handleFileSelect}
      />
    </div>
  );
};

export default ProfilePictureAvatar;
